import { z } from "zod";

const minor = z.number().int("Montant invalide").min(0, "Montant invalide");

export const productImageSchema = z.object({
  url: z.string().url("URL d'image invalide").max(1000),
  alt: z.string().max(200).optional().default(""),
  fileId: z.string().regex(/^[a-f0-9]{24}$/i).nullable().optional(),
});

export const productVariantSchema = z.object({
  _id: z.string().regex(/^[a-f0-9]{24}$/i).optional(),
  sku: z.string().trim().max(80).optional().default(""),
  title: z.string().trim().min(1, "Nom de variante requis").max(120),
  options: z.record(z.string().max(60), z.string().max(80)).optional().default({}),
  priceMinor: minor.nullable().optional(),
  compareAtPriceMinor: minor.nullable().optional(),
  costMinor: minor.nullable().optional(),
  stock: z.number().int().min(0).optional().default(0),
  imageUrl: z.string().url().max(1000).nullable().optional(),
  active: z.boolean().optional().default(true),
});

export const quantityOfferSchema = z.object({
  qty: z.number().int().min(2, "Quantité minimum 2").max(100),
  totalPriceMinor: minor,
  label: z.string().trim().max(80).optional().default(""),
});

export const productInputSchema = z.object({
  name: z.string().trim().min(2, "Nom trop court").max(200),
  sku: z.string().trim().max(80).optional().default(""),
  description: z.string().max(20_000).optional().default(""),
  shortDescription: z.string().max(500).optional().default(""),
  images: z.array(productImageSchema).max(20).optional().default([]),
  videoUrl: z.string().url().max(1000).nullable().optional(),
  priceMinor: minor,
  compareAtPriceMinor: minor.nullable().optional(),
  costMinor: minor.nullable().optional(),
  status: z.enum(["active", "draft", "archived"]).optional().default("active"),
  category: z.string().trim().max(80).optional().default(""),
  tags: z.array(z.string().trim().max(40)).max(30).optional().default([]),
  brand: z.string().trim().max(80).optional().default(""),
  trackInventory: z.boolean().optional().default(true),
  stock: z.number().int().min(0).optional().default(0),
  lowStockThreshold: z.number().int().min(0).max(100_000).optional().default(5),
  optionNames: z.array(z.string().trim().max(40)).max(3).optional().default([]),
  variants: z.array(productVariantSchema).max(100).optional().default([]),
  quantityOffers: z.array(quantityOfferSchema).max(5).optional().default([]),
  upsellProductIds: z.array(z.string().regex(/^[a-f0-9]{24}$/i)).max(10).optional().default([]),
  weightGrams: z.number().int().min(0).max(1_000_000).nullable().optional(),
  seo: z
    .object({ title: z.string().max(70).optional().default(""), description: z.string().max(170).optional().default("") })
    .optional()
    .default({}),
});
export type ProductInput = z.infer<typeof productInputSchema>;

export const productUpdateSchema = productInputSchema.partial();
export type ProductUpdate = z.infer<typeof productUpdateSchema>;

export const productListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum(["active", "draft", "archived", "all"]).default("all"),
  category: z.string().trim().max(80).optional(),
  stock: z.enum(["all", "low", "out"]).default("all"),
  sort: z.enum(["recent", "name", "price", "stock", "sales", "revenue"]).default("recent"),
});
export type ProductListQuery = z.infer<typeof productListQuery>;

export const stockAdjustSchema = z.object({
  delta: z.number().int().refine((v) => v !== 0, "La variation ne peut pas être nulle"),
  variantId: z.string().regex(/^[a-f0-9]{24}$/i).nullable().optional(),
  note: z.string().max(300).optional().default(""),
});

/** Product as returned by the API. */
export interface ProductDto {
  id: string;
  name: string;
  slug: string;
  sku: string;
  description: string;
  shortDescription: string;
  images: Array<{ url: string; alt: string }>;
  videoUrl: string | null;
  priceMinor: number;
  compareAtPriceMinor: number | null;
  costMinor: number | null;
  currency: string;
  status: "active" | "draft" | "archived";
  category: string;
  tags: string[];
  brand: string;
  trackInventory: boolean;
  stock: number;
  totalStock: number;
  lowStockThreshold: number;
  optionNames: string[];
  variants: Array<{
    id: string;
    sku: string;
    title: string;
    options: Record<string, string>;
    priceMinor: number | null;
    compareAtPriceMinor: number | null;
    costMinor: number | null;
    stock: number;
    imageUrl: string | null;
    active: boolean;
  }>;
  quantityOffers: Array<{ qty: number; totalPriceMinor: number; label: string }>;
  upsellProductIds: string[];
  weightGrams: number | null;
  seo: { title: string; description: string };
  metaCatalog: { status: string; syncedAt: string | null; error: string | null };
  stats: { views: number; ordersCount: number; unitsSold: number; revenueMinor: number; returnsCount: number; lastOrderAt: string | null };
  stockState: "ok" | "low" | "out" | "untracked";
  createdAt: string;
  updatedAt: string;
}
